import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { io, type Socket } from "socket.io-client";
import { getOrders, updateOrderStatus, getVenue } from "@/api/endpoints";
import type { Order, OrderStatus } from "@/types";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChefHat, Clock, ChevronRight } from "lucide-react";
import { playSound, ORDER_SOUNDS } from "@/utils/notification-sounds";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3004";

const COLUMNS: Array<{
  status: Extract<OrderStatus, "pending" | "preparing" | "ready">;
  labelKey: "status_pending" | "status_preparing" | "status_ready";
  next: OrderStatus;
  nextLabelKey: "mark_preparing" | "mark_ready" | "mark_delivered";
  accent: string;
}> = [
  { status: "pending", labelKey: "status_pending", next: "preparing", nextLabelKey: "mark_preparing", accent: "bg-amber-500" },
  { status: "preparing", labelKey: "status_preparing", next: "ready", nextLabelKey: "mark_ready", accent: "bg-sky-500" },
  { status: "ready", labelKey: "status_ready", next: "delivered", nextLabelKey: "mark_delivered", accent: "bg-emerald-500" },
];

function timeAgo(iso: string, t: (k: "kitchen_just_now" | "kitchen_minutes_ago" | "kitchen_hours_ago") => string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return t("kitchen_just_now");
  if (mins < 60) return t("kitchen_minutes_ago").replace("{{n}}", String(mins));
  const hrs = Math.floor(mins / 60);
  return t("kitchen_hours_ago").replace("{{n}}", String(hrs));
}

export function KitchenPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const { t } = useI18n();
  const qc = useQueryClient();
  const lastSeenRef = useRef<Set<string>>(new Set());

  const { data: venue } = useQuery({
    queryKey: ["venue", venueId],
    queryFn: () => getVenue(venueId!),
    enabled: !!venueId,
  });

  const orderSoundKey = venue?.notificationSounds?.orderSound || "kaching";

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["orders", venueId, "active"],
    queryFn: () => getOrders(venueId!, "active"),
    enabled: !!venueId,
    refetchInterval: 10_000, // socket-driven, but poll as a safety net
  });

  // Audio cue when a brand-new order shows up. Compares against the previous
  // render's id set so reloads / refetches don't re-play the sound.
  useEffect(() => {
    if (!orders.length) return;
    const seen = lastSeenRef.current;
    const fresh = orders.filter((o) => !seen.has(o.id || o._id || ""));
    if (fresh.length > 0 && seen.size > 0) {
      // Only play if we'd already populated once — first load is silent.
      playSound(ORDER_SOUNDS, orderSoundKey);
    }
    lastSeenRef.current = new Set(orders.map((o) => o.id || o._id || ""));
  }, [orders, orderSoundKey]);

  // Realtime subscription. New / updated orders trigger a refetch instead of
  // mutating the cache by hand — keeps a single source of truth.
  useEffect(() => {
    if (!venueId) return;
    const socket: Socket = io(API_BASE, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
    });

    const join = () => socket.emit("join:venue", venueId);
    join();
    socket.on("connect", join);
    socket.on("order:new", () => {
      qc.invalidateQueries({ queryKey: ["orders", venueId, "active"] });
    });
    socket.on("order:updated", () => {
      qc.invalidateQueries({ queryKey: ["orders", venueId, "active"] });
    });

    return () => {
      socket.emit("leave:venue", venueId);
      socket.disconnect();
    };
  }, [venueId, qc]);

  const advanceMutation = useMutation({
    mutationFn: ({ orderId, status }: { orderId: string; status: OrderStatus }) =>
      updateOrderStatus(orderId, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders", venueId, "active"] }),
  });

  // Bucket orders by status for the kanban columns. Anything outside the three
  // active states is ignored (e.g. a stale "delivered" if the backend slips one
  // through before the cache flushes).
  const byStatus: Record<string, Order[]> = { pending: [], preparing: [], ready: [] };
  for (const o of orders) {
    if (o.status in byStatus) byStatus[o.status].push(o);
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <ChefHat className="size-6" />
          {t("kitchen")}
        </h1>
        <span className="text-sm text-muted-foreground">
          {orders.length} {t("kitchen_active_count")}
        </span>
      </div>

      {isLoading ? (
        <p className="py-8 text-center text-muted-foreground">{t("loading")}</p>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-20">
          <ChefHat className="mb-4 size-12 text-muted-foreground" />
          <p className="text-muted-foreground">{t("kitchen_no_orders")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {COLUMNS.map((col) => (
            <div key={col.status} className="rounded-lg border bg-muted/30 p-3">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`size-2 rounded-full ${col.accent}`} />
                  <h2 className="font-semibold">{t(col.labelKey)}</h2>
                </div>
                <Badge variant="secondary">{byStatus[col.status].length}</Badge>
              </div>

              <div className="space-y-3">
                {byStatus[col.status].length === 0 ? (
                  <p className="py-6 text-center text-xs text-muted-foreground">—</p>
                ) : (
                  byStatus[col.status].map((order) => (
                    <Card key={order.id || order._id} className="overflow-hidden">
                      <CardContent className="p-4">
                        <div className="mb-2 flex items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-base font-bold">{order.table}</p>
                              {order.source === "trendyolgo" && (
                                <Badge className="border-transparent bg-orange-500 text-white hover:bg-orange-500">
                                  Trendyol GO
                                </Badge>
                              )}
                            </div>
                            <p className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="size-3" />
                              {timeAgo(order.createdAt, t)}
                            </p>
                          </div>
                          <p className="shrink-0 text-sm font-bold text-primary">
                            {order.totalPrice.toLocaleString("tr-TR")} ₺
                          </p>
                        </div>

                        <ul className="mb-3 space-y-1 text-sm">
                          {order.items.map((it, idx) => (
                            <li key={idx} className="flex justify-between gap-2">
                              <span>
                                <span className="font-semibold">{it.quantity}×</span>{" "}
                                {it.name}
                                {it.variantName && (
                                  <span className="text-muted-foreground"> ({it.variantName})</span>
                                )}
                                {it.note && (
                                  <span className="block text-xs italic text-amber-600">
                                    “{it.note}”
                                  </span>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>

                        {order.note && (
                          <p className="mb-3 rounded-md bg-amber-50 p-2 text-xs italic text-amber-900">
                            {order.note}
                          </p>
                        )}

                        <Button
                          size="sm"
                          className="w-full"
                          disabled={advanceMutation.isPending}
                          onClick={() =>
                            advanceMutation.mutate({
                              orderId: order.id || order._id || "",
                              status: col.next,
                            })
                          }
                        >
                          {t(col.nextLabelKey)}
                          <ChevronRight className="ml-1 size-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
